import React from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Divider,
  Chip,
  Paper,
} from '@material-ui/core';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { formatTimestamp } from '../../utils/formatters';
import { modelOverviewStyles } from './styles/styles';
import { ModelOverviewProps } from './types';

export const MLModelOverview = ({ mlFlowModelDetail }: ModelOverviewProps) => {
  const styles = modelOverviewStyles;
  const configApi = useApi(configApiRef);
  const mlflowBaseUrl = configApi.getString('mlflow.baseUrl');
  const model = mlFlowModelDetail.model;
  const runInfo = mlFlowModelDetail.runInfo;

  return (
    <>
      <Typography variant="h6" style={{ marginBottom: 6 }}>
        Model
      </Typography>
      <Typography
        variant="subtitle2"
        style={{ ...styles.label, marginBottom: 2 }}
      >
        Name
      </Typography>
      <Typography style={{ ...styles.valuePrimary, marginBottom: 10 }}>
        <a
          href={`${mlflowBaseUrl}/#/models/${model.name}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {model.name}
          {/* icon open in new page */}
          <OpenInNew
            style={{ fontSize: 16, marginLeft: 4, verticalAlign: 'middle' }}
          />
        </a>
      </Typography>

      <Typography
        variant="subtitle2"
        style={{ ...styles.label, marginBottom: 2 }}
      >
        Description
      </Typography>
      <Typography style={{ ...styles.smallValue, marginBottom: 12 }}>
        {model.description || '-'}
      </Typography>

      <Divider style={{ margin: '8px 0 12px' }} />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography
            variant="subtitle2"
            style={{ ...styles.label, marginBottom: 2 }}
          >
            Created
          </Typography>
          <Typography style={styles.smallValue}>
            {formatTimestamp(model.creation_timestamp)}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography
            variant="subtitle2"
            style={{ ...styles.label, marginBottom: 2 }}
          >
            Last updated
          </Typography>
          <Typography style={styles.smallValue}>
            {formatTimestamp(model.last_updated_timestamp)}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography
            variant="subtitle2"
            style={{ ...styles.label, marginBottom: 2 }}
          >
            Aliases
          </Typography>
          <Typography style={styles.smallValue}>
            {(model.aliases || [])
              .map((a: any) => a.alias ?? JSON.stringify(a))
              .join(', ') || '-'}
          </Typography>
        </Grid>

        {model.tags && model.tags.length !== 0 && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              style={{ ...styles.label, marginBottom: 6 }}
            >
              Tags
            </Typography>
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 8,
              }}
            >
              {(model.tags || []).map((t: any) => (
                <Chip
                  key={t.key}
                  label={`${t.key}: ${t.value}`}
                  size="small"
                  style={{ background: '#eef2ff', color: '#1f2937' }}
                />
              ))}
            </Box>
            <Paper variant="outlined" style={{ borderRadius: 8 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold', width: '40%' }}>
                      Name
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(model.tags || []).map((t: any) => (
                    <TableRow key={t.key}>
                      <TableCell>{t.key}</TableCell>
                      <TableCell>{t.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {/* Run Info Section */}
        {runInfo && (
          <Grid item xs={12}>
            <Divider style={{ margin: '8px 0 12px' }} />
            <Typography variant="h6" style={{ marginBottom: 6 }}>
              Run info
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  style={{ ...styles.label, marginBottom: 2 }}
                >
                  Run name
                </Typography>
                <Typography style={styles.smallValue}>
                  {runInfo?.info?.run_name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  style={{ ...styles.label, marginBottom: 2 }}
                >
                  Status
                </Typography>
                <Typography style={styles.smallValue}>
                  {runInfo?.info?.status || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  style={{ ...styles.label, marginBottom: 2 }}
                >
                  Experiment ID
                </Typography>
                <Typography style={styles.smallValue}>
                  {runInfo?.info?.experiment_id || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  style={{ ...styles.label, marginBottom: 2 }}
                >
                  Run ID
                </Typography>
                <Typography
                  style={{
                    ...styles.smallValue,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  }}
                >
                  {runInfo?.info?.run_id || '-'}
                </Typography>
              </Grid>
            </Grid>

            {/* Key Metrics */}
            <Typography variant="h6" style={{ marginTop: 10, marginBottom: 6 }}>
              Metrics
            </Typography>
            {runInfo?.data?.metrics && runInfo.data.metrics.length > 0 ? (
              <>
                <Typography variant="caption" style={{ marginTop: 10, marginBottom: 6 }}>
                  This metric info is extracted from the latest model version's run.
                </Typography>
                <Paper
                  variant="outlined"
                  style={{ borderRadius: 8, overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 'bold', width: '40%' }}>
                          Key
                        </TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runInfo.data.metrics.map((m: any) => (
                        <TableRow key={m.key}>
                          <TableCell
                            style={{
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            }}
                          >
                            {m.key}
                          </TableCell>
                          <TableCell>{m.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            ) : (
              <Typography variant="body2" style={{ color: '#777' }}>
                No metric info available
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};
